import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ICONS } from './nav';
import { useApi } from './kit';
import { useBoard } from './board';
import { TestModeToggle, WorkspaceSwitcher } from './workspace';
import { openedDirectly } from './phone';

// Phones only (App renders these when usePhone() is true): the tab bar at the bottom (Today, Chats, Inbox, Campaigns,
// More), the More sheet with the rest of the places, the cards of a thread page as a sheet under the chat, and a
// way back on pages opened straight from a link.

const MORE_ICON = <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="19" cy="12" r="1.9" /></svg>;

type Tab = { to: string; label: string; icon: ReactNode; badge?: number; end?: boolean; also?: string[] };

export function PhoneTabBar({ inboxBadge }: { inboxBadge: number }) {
  const { pathname } = useLocation();
  const threads = useApi<{ data: Array<{ unfinished: string | null }> }>('/portal/threads', { refetchMs: 30_000 });
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [pathname]);
  const tabs: Tab[] = [
    { to: '/', label: 'Today', icon: ICONS.overview, end: true },
    { to: '/chats', label: 'Chats', icon: ICONS.chats, badge: (threads.data?.data ?? []).filter((t) => t.unfinished).length, also: ['/chat'] },
    { to: '/inbox', label: 'Inbox', icon: ICONS.work, badge: inboxBadge, also: ['/approvals/', '/handoffs', '/replies'] },
    { to: '/campaigns', label: 'Campaigns', icon: ICONS.campaigns, also: ['/work', '/audiences', '/ad-campaigns', '/reports'] },
  ];
  const on = (t: Tab) => (t.end ? pathname === t.to : pathname === t.to || pathname.startsWith(`${t.to}/`)) || (t.also ?? []).some((p) => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`));
  const anyOn = tabs.some(on);
  return (
    <>
      <nav className="tabbar" aria-label="Main tabs">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={on(t) ? 'active' : ''} aria-current={on(t) ? 'page' : undefined}
            aria-label={t.badge ? `${t.label}, ${t.badge} waiting` : t.label}>
            {t.icon}<span>{t.label}</span>
            {t.badge ? <span className="tb-badge" aria-hidden="true">{t.badge > 99 ? '99+' : t.badge}</span> : null}
          </NavLink>
        ))}
        <button type="button" className={more || !anyOn ? 'active' : ''} aria-haspopup="dialog" aria-expanded={more} onClick={() => setMore(!more)}>
          {MORE_ICON}<span>More</span>
        </button>
      </nav>
      {more ? <MoreSheet onClose={() => setMore(false)} /> : null}
    </>
  );
}

// what the desktop sidebar and account menu offer, so a phone reaches it too (Integrations, Notifications, Audit log,
// Stop spending and Two-factor were missing, 2026-10-30)
const MORE: Array<[string, string, string]> = [
  ['/accounts', 'Accounts', 'customers'], ['/approvals', 'Approvals', 'compliance'], ['/context', 'Company & ICP', 'setup'], ['/work', 'All work', 'queues'],
  ['/integrations', 'Integrations', 'plug'], ['/settings', 'Settings', 'settings'], ['/billing', 'Billing', 'growth'], ['/notifications', 'Notifications', 'account'],
  ['/audit', 'Audit log', 'compliance'], ['/kill-switches', 'Stop spending', 'compliance'], ['/mfa', 'Two-factor', 'account'], ['/developers', 'Developers', 'platform'],
];

function MoreSheet({ onClose }: { onClose: () => void }) {
  const { session, staff, signOut } = useAuth();
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', esc);
  }, []);
  return (
    <div className="more-sheet" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ms-panel" role="dialog" aria-modal="true" aria-label="More" tabIndex={-1} ref={panel}>
        <div className="ms-head"><b>More</b><button type="button" className="link" onClick={onClose}>Close</button></div>
        <ul className="ms-links">
          {MORE.map(([to, label, icon]) => <li key={to}><Link to={to} onClick={onClose}>{ICONS[icon]}<span>{label}</span></Link></li>)}
          <li><button type="button" onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('olv-support')); }}>{ICONS.chats}<span>Support</span></button></li>
          {staff ? <li><Link to="/ops" onClick={onClose}>{ICONS.compliance}<span>Admin panel</span></Link></li> : null}
        </ul>
        <div className="ms-ws"><WorkspaceSwitcher inline onDone={onClose} /><TestModeToggle /></div>
        <div className="ms-account">
          <span className="ms-label">Account</span>
          <span className="ms-email">{session?.user.email ?? ''}</span>
          <span className="ms-row">
            <Link className="btn btn-sm" to="/refer" onClick={onClose}>Refer and earn</Link>
            <button type="button" className="btn btn-sm" onClick={() => void signOut()}>Sign out</button>
          </span>
        </div>
      </div>
    </div>
  );
}

/** Where the way back from a thread leads: Today if it was opened from a link, else its list. */
export function backFor(pathname: string): { to: string; label: string } {
  if (openedDirectly()) return { to: '/', label: 'Today' };
  return pathname.startsWith('/campaigns') ? { to: '/campaigns', label: 'Campaigns' } : { to: '/chats', label: 'Chats' };
}

/** A focused page (an approval, an account) on a phone: a small bar back, to Today when opened from a link. */
const FOCUSED = /^\/((approvals|accounts|import|competitors)\/[^/]+|icp\/versions\/[^/]+|handoffs|replies)$/;
export function PhoneBack() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  if (!FOCUSED.test(pathname)) return null;
  return (
    <div className="phone-back">
      {openedDirectly() ? <Link to="/">← Today</Link> : <button type="button" className="link" onClick={() => nav(-1)}>← Back</button>}
    </div>
  );
}

type Cardish = { key: string; title: string; status: string };
/** "3 of 7 cards · Markets needs you": the cards of this thread in one line, for the closed sheet. */
function useCardsLine(pathname: string, enabled: boolean) {
  const chatId = pathname.match(/^\/chat\/([0-9a-f-]{36})/)?.[1] ?? null;
  const campaign = pathname.match(/^\/campaigns\/([0-9a-f-]{36})/)?.[1] ?? null;
  const t = useApi<{ chat: { brief?: { cards: Cardish[] } | null } | null }>(enabled && chatId ? `/portal/assistant?thread=${chatId}` : null);
  const b = useBoard(campaign, enabled && !chatId);
  const cards: Cardish[] = (chatId ? t.data?.chat?.brief?.cards : b.data?.cards) ?? [];
  if (!cards.length) return 'The cards';
  const done = cards.filter((c) => c.status === 'done' || c.status === 'skipped').length;
  const blocked = cards.find((c) => c.status === 'blocked');
  const working = cards.find((c) => c.status === 'working');
  return `${done} of ${cards.length} cards${blocked ? ` · ${blocked.title} needs you` : working ? ` · ${working.title}: working` : ''}`;
}

/**
 * The page under the chat on a phone thread: closed it is a line at the bottom ("3 of 7 cards · …"); a tap or a drag
 * up opens it to full height, a tap or a drag down closes it. The chat's "↳ card" and "Fill … myself" open it on
 * that card (event olv-cards-sheet). Off a thread page it is the plain page column.
 */
export function CardsSheet({ on, children }: { on: boolean; children: ReactNode }) {
  const { pathname } = useLocation();
  const [full, setFull] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);
  const start = useRef<number | null>(null);
  const moved = useRef(false);
  useEffect(() => setFull(false), [pathname]);
  useEffect(() => {
    const f = (e: Event) => setFull((e as CustomEvent<'full' | 'peek'>).detail !== 'peek');
    window.addEventListener('olv-cards-sheet', f);
    return () => window.removeEventListener('olv-cards-sheet', f);
  }, []);
  useEffect(() => { document.documentElement.classList.toggle('cards-full', on && full); }, [on, full]);
  const line = useCardsLine(pathname, on);
  const up = () => {
    const dy = drag ?? 0;
    start.current = null; setDrag(null);
    if (Math.abs(dy) < 6) return;
    moved.current = true;
    if (dy < -40) setFull(true); else if (dy > 40) setFull(false);
  };
  const style = drag === null ? undefined : { transform: full ? `translateY(${Math.max(0, drag)}px)` : `translateY(calc(100% - var(--peek) + ${Math.min(0, drag)}px))`, transition: 'none' };
  return (
    <div className={on ? `cards-sheet${full ? ' full' : ''}` : undefined} style={on ? style : undefined} role={on ? 'region' : undefined} aria-label={on ? 'Cards' : undefined}>
      {on ? (
        <div className="cs-handle"
          onPointerDown={(e) => { start.current = e.clientY; moved.current = false; (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); }}
          onPointerMove={(e) => { if (start.current !== null) setDrag(e.clientY - start.current); }}
          onPointerUp={up} onPointerCancel={up}>
          <button type="button" className="cs-toggle" aria-expanded={full} onClick={() => { if (moved.current) { moved.current = false; return; } setFull(!full); }}>
            <span className="cs-grip" aria-hidden="true" />
            <span className="cs-line">{line}</span>
            <span className="cs-act">{full ? 'Back to the chat' : 'Open'}</span>
          </button>
        </div>
      ) : null}
      {children}
    </div>
  );
}
